import React, { useId } from 'react';
import {
  Alert,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

export interface ChapterGivingPyramidRow {
  chapter_abbreviation: string;
  chapter_name: string;
  spd: string;
  year: number;
  international_amount: number;
  spd_amount: number;
  local_amount: number;
  international_percent: number;
  spd_percent: number;
  local_percent: number;
  is_total?: boolean;
}

const LOCAL_COLOR = '#c62828';
const SPD_COLOR = '#f9a825';
const INTERNATIONAL_COLOR = '#2e7d32';

const LEVELS = [
  {
    key: 'local' as const,
    title: 'Local giving',
    color: LOCAL_COLOR,
    text: '#ffffff',
    definition:
      'Community support, such as honorariums for chapter speakers, small gifts to project recipients, and local scholarships. Local scholarships are allowed. International Chapter does not encourage them, and this local giving should stay smaller than gifts to International and S/P/D projects combined.',
  },
  {
    key: 'spd' as const,
    title: 'S/P/D projects',
    color: SPD_COLOR,
    text: '#3e2723',
    definition: 'Gifts to state, provincial, and district chapter projects.',
  },
  {
    key: 'international' as const,
    title: 'International projects',
    color: INTERNATIONAL_COLOR,
    text: '#ffffff',
    definition:
      'Gifts that support International Chapter projects, including the Educational Loan Fund, the International Peace Scholarship Fund, the Program for Continuing Education, Scholar Awards, the STAR Scholarship, and Cottey College.',
  },
];

function money(amount: number): string {
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function percentLabel(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
}

function membershipYearLabel(endingYear: number): string {
  if (!endingYear) return '';
  const start = endingYear - 1;
  return `${start}-${String(endingYear).slice(-2)}`;
}

const ChapterGivingPyramid: React.FC<{ row: ChapterGivingPyramidRow }> = ({
  row,
}) => {
  const clipId = useId().replace(/:/g, '');
  const total = row.international_amount + row.spd_amount + row.local_amount;
  const localExceeds =
    row.local_amount > row.international_amount + row.spd_amount;

  const top = 8;
  const height = 228;
  const bottom = top + height;
  const left = 18;
  const right = 222;
  const mid = (left + right) / 2;

  const localFrac = total > 0 ? row.local_percent / 100 : 0;
  const spdFrac = total > 0 ? row.spd_percent / 100 : 0;
  const yLocal = top + Math.sqrt(Math.min(1, Math.max(0, localFrac))) * height;
  const ySpd =
    top + Math.sqrt(Math.min(1, Math.max(0, localFrac + spdFrac))) * height;

  const bands = [
    {
      key: 'local',
      y: top,
      y2: yLocal,
      color: LOCAL_COLOR,
      text: '#ffffff',
      label: percentLabel(row.local_percent),
    },
    {
      key: 'spd',
      y: yLocal,
      y2: ySpd,
      color: SPD_COLOR,
      text: '#3e2723',
      label: percentLabel(row.spd_percent),
    },
    {
      key: 'international',
      y: ySpd,
      y2: bottom,
      color: INTERNATIONAL_COLOR,
      text: '#ffffff',
      label: percentLabel(row.international_percent),
    },
  ];

  const amounts: Record<(typeof LEVELS)[number]['key'], number> = {
    local: row.local_amount,
    spd: row.spd_amount,
    international: row.international_amount,
  };
  const percents: Record<(typeof LEVELS)[number]['key'], number> = {
    local: row.local_percent,
    spd: row.spd_percent,
    international: row.international_percent,
  };

  const chapterLabel = [row.spd, row.chapter_abbreviation]
    .filter(Boolean)
    .join(' ');

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" fontWeight={700}>
          {chapterLabel ? `${chapterLabel} — ` : ''}
          {row.chapter_name || 'Chapter'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Membership year {membershipYearLabel(row.year)} · Total {money(total)}
        </Typography>
      </Box>

      {localExceeds && (
        <Alert severity="warning">
          Local giving is larger than International and S/P/D giving combined.
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={4}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, mx: 'auto' }}>
          <svg
            viewBox="0 0 240 250"
            width="100%"
            role="img"
            aria-label={`Giving pyramid. Local ${percentLabel(row.local_percent)}, S/P/D ${percentLabel(row.spd_percent)}, International ${percentLabel(row.international_percent)}.`}
          >
            <defs>
              <clipPath id={clipId}>
                <polygon
                  points={`${mid},${top} ${left},${bottom} ${right},${bottom}`}
                />
              </clipPath>
            </defs>
            <g clipPath={`url(#${clipId})`}>
              {bands.map((band) =>
                band.y2 - band.y > 0.5 ? (
                  <rect
                    key={band.key}
                    x={0}
                    y={band.y}
                    width={240}
                    height={band.y2 - band.y}
                    fill={band.color}
                  />
                ) : null
              )}
            </g>
            <polygon
              points={`${mid},${top} ${left},${bottom} ${right},${bottom}`}
              fill="none"
              stroke="#4a148c"
              strokeWidth={3}
              strokeLinejoin="round"
            />
            {bands.map((band) => {
              const bandHeight = band.y2 - band.y;
              if (bandHeight < 28) return null;
              return (
                <text
                  key={`${band.key}-label`}
                  x={mid}
                  y={(band.y + band.y2) / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={band.text}
                  fontSize={bandHeight < 40 ? 13 : 16}
                  fontWeight={700}
                >
                  {band.label}
                </text>
              );
            })}
          </svg>
        </Box>

        <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
          {LEVELS.map((level) => (
            <Stack key={level.key} direction="row" spacing={1.5}>
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  mt: 0.4,
                  flexShrink: 0,
                  bgcolor: level.color,
                  borderRadius: 0.5,
                }}
              />
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {level.title} · {percentLabel(percents[level.key])} ·{' '}
                  {money(amounts[level.key])}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {level.definition}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export const ChapterGivingPyramidResults: React.FC<{
  rows: ChapterGivingPyramidRow[];
}> = ({ rows }) => {
  const chapters = rows.filter((row) => !row.is_total);
  if (chapters.length === 1) {
    return <ChapterGivingPyramid row={chapters[0]} />;
  }
  return <ChapterGivingPyramidTable rows={rows} />;
};

const ChapterGivingPyramidTable: React.FC<{
  rows: ChapterGivingPyramidRow[];
}> = ({ rows }) => {
  const year = rows.find((row) => row.year)?.year;
  return (
    <Stack spacing={2}>
      {year ? (
        <Typography variant="body2" color="text.secondary">
          Membership year {membershipYearLabel(year)}
        </Typography>
      ) : null}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>S/P/D</TableCell>
              <TableCell>Chapter</TableCell>
              <TableCell>Chapter Name</TableCell>
              <TableCell align="right">International</TableCell>
              <TableCell align="right">International %</TableCell>
              <TableCell align="right">S/P/D</TableCell>
              <TableCell align="right">S/P/D %</TableCell>
              <TableCell align="right">Local</TableCell>
              <TableCell align="right">Local %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow
                key={
                  row.is_total
                    ? 'total'
                    : `${row.chapter_abbreviation}-${row.chapter_name}-${i}`
                }
                sx={row.is_total ? { '& td': { fontWeight: 700 } } : undefined}
              >
                <TableCell>{row.spd || '—'}</TableCell>
                <TableCell>
                  {row.is_total ? '' : row.chapter_abbreviation || '—'}
                </TableCell>
                <TableCell>{row.chapter_name || '—'}</TableCell>
                <TableCell align="right">
                  {money(row.international_amount)}
                </TableCell>
                <TableCell align="right">
                  {percentLabel(row.international_percent)}
                </TableCell>
                <TableCell align="right">{money(row.spd_amount)}</TableCell>
                <TableCell align="right">
                  {percentLabel(row.spd_percent)}
                </TableCell>
                <TableCell align="right">{money(row.local_amount)}</TableCell>
                <TableCell align="right">
                  {percentLabel(row.local_percent)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default ChapterGivingPyramid;
